import ShowImportPage from './pages/ShowImportPage'
import ShowTransferPage from './pages/ShowTransferPage'
import HeroAwardReportPage from '@/pages/HeroAwardReportPage'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { ACCESS_LINK_PATH } from '@schemas'
import { AppShell } from './layouts/AppShell'
import LandingPage from './pages/LandingPage'
import ShowsPage from './pages/ShowsPage'
import OrganizationsPage from './pages/OrganizationsPage'
import SetupPage from './pages/SetupPage'
import ModelClassesPage from './pages/ModelClassesPage'
import ShowClassesPage from './pages/ShowClassesPage'
import SeriesAlignmentPage from './pages/SeriesAlignmentPage'
import SeriesLayout from './layouts/SeriesLayout'
import SeriesSettingsPage from './pages/SeriesSettingsPage'
import LegacySeriesRedirect from './router/LegacySeriesRedirect'
import ShowSeriesRedirect from './router/ShowSeriesRedirect'
import SeriesTrendReportPage from './pages/SeriesTrendReportPage'
import ShowSeriesPage from './pages/ShowSeriesPage'
import ExhibitorsPageNew from './pages/ExhibitorsPageNew'
import EntriesPage from './pages/EntriesPage'
import EntryDetailPage from './pages/EntryDetailPage'
import EntrantPage from './pages/EntrantPage'
import EntrantsPage from './pages/EntrantsPage'
import CheckInPage from './pages/CheckInPage'
import StatusPage from './pages/StatusPage'
import JudgingPage from './pages/JudgingPage'
import AwardsPage from './pages/AwardsPage'
import ReportsPage from './pages/ReportsPage'
import ClubParticipationPage from './pages/reports/ClubParticipationPage'
import ClubsPage from './pages/ClubsPage'
import JudgeSpeakingReportPage from './pages/JudgeSpeakingReportPage'
import DisplayResultsReportPage from './pages/DisplayResultsReportPage'
import ShowResultsReportPage from './pages/ShowResultsReportPage'
import ReportLayoutDemoPage from './pages/ReportLayoutDemoPage'
import MemberAwardCardsPage from './pages/MemberAwardCardsPage'
import StaffRosterReportPage from './pages/StaffRosterReportPage'
import StaffCertificatesReportPage from './pages/StaffCertificatesReportPage'
import ClubParticipationReportPage from './pages/ClubParticipationReportPage'
import NotFoundPage from './pages/NotFoundPage'
import RunDashboard from './pages/RunDashboard'
import ShowDayBoardPage from './pages/ShowDayBoardPage'
import PeoplePage from './pages/PeoplePage'
import { ShowSlugResolver, ShowSlugStatusRedirect } from './router/ShowSlugResolver'
import RequireAuth from './router/RequireAuth'
import { LegacyShowRedirect } from './router/LegacyShowRedirect'
import { LabelOnlyAllowed } from './router/LabelOnlyAllowed'
import ChangePasswordPage from './pages/account/ChangePassword'
import MyAccountPage from './pages/account/MyAccountPage'
import AdminUsersPage from './pages/admin/UsersPage'
import NewShowWizard from './pages/shows/NewShowWizard'

export const routes: RouteObject[] = [
  {
    path: '/public/show-series/:slug/championships',
    lazy: async () => ({ Component: (await import('./pages/PublicChampionshipsPage')).default }),
    hydrateFallbackElement: <p role="status">Loading championships…</p>,
  },
  {
    path: '/public/classes/:classKey',
    lazy: async () => ({ Component: (await import('./pages/PublicClassGuidePage')).default }),
    hydrateFallbackElement: <p role="status">Loading class guide…</p>,
  },
  {
    path: '/public/shows',
    lazy: async () => ({ Component: (await import('./pages/PublicAtlasPage')).default }),
    hydrateFallbackElement: <p role="status">Loading shows…</p>,
  },
  {
    path: '/public/show-series/:slug',
    lazy: async () => ({ Component: (await import('./pages/PublicSeriesReportPage')).default }),
    hydrateFallbackElement: <p role="status">Loading series…</p>,
  },
  {
    path: '/public/shows/:slug',
    lazy: async () => ({ Component: (await import('./pages/PublicShowResultsPage')).default }),
    hydrateFallbackElement: <p role="status">Loading results…</p>,
  },
  {
    path: '/public/shows/:slug/highlights',
    lazy: async () => ({ Component: (await import('./pages/PublicShowHighlightsPage')).default }),
    hydrateFallbackElement: <p role="status">Loading results…</p>,
  },
  {
    path: '/public/shows/:slug/championship',
    lazy: async () => ({ Component: (await import('./pages/PublicChampionshipPage')).default }),
    hydrateFallbackElement: <p role="status">Loading results…</p>,
  },
  {
    path: '/connect/server',
    lazy: async () => ({ Component: (await import('./pages/ServerPage')).default }),
    hydrateFallbackElement: <p role="status">Loading server settings…</p>,
  },
  {
    path: '/login',
    lazy: async () => ({ Component: (await import('./pages/LoginPage')).default }),
    hydrateFallbackElement: <p role="status">Loading sign-in…</p>,
  },
  {
    // Under the reserved `account` address, so no show's address can collide with it.
    path: '/account/forgot-password',
    lazy: async () => ({
      Component: (await import('./pages/account/ForgotPasswordPage')).default,
    }),
    hydrateFallbackElement: <p role="status">Loading…</p>,
  },
  // Setup and reset links, opened while signed out.
  {
    path: ACCESS_LINK_PATH,
    lazy: async () => ({
      Component: (await import('./pages/account/AccountAccessPage')).default,
    }),
    hydrateFallbackElement: <p role="status">Loading…</p>,
  },
  // Prefer explicit show sections over an equally ranked series-ID route.
  {
    path: '/:showSlug',
    element: (
      <RequireAuth>
        <ShowSlugResolver />
      </RequireAuth>
    ),
    children: [
      {
        path: '',
        element: <AppShell />,
        children: [
          { index: true, element: <ShowSlugStatusRedirect /> },
          { path: 'classes', element: <ShowClassesPage /> },
          { path: 'show-classes', element: <ShowClassesPage /> },
          { path: 'setup', element: <SetupPage /> },
          { path: 'series-alignment', element: <LegacySeriesRedirect section="alignment" /> },
          { path: 'series-report', element: <LegacySeriesRedirect section="reports" /> },
          { path: 'exhibitors', element: <ExhibitorsPageNew /> },
          { path: 'run', element: <RunDashboard /> },
          {
            path: '',
            element: <LabelOnlyAllowed />,
            children: [
              { path: 'run/board', element: <ShowDayBoardPage /> },
              {
                path: 'awards',
                element: (
                  <RequireAuth>
                    <AwardsPage />
                  </RequireAuth>
                ),
              },
              { path: 'check-in', element: <CheckInPage /> },
              {
                path: 'entries',
                element: (
                  <RequireAuth>
                    <EntriesPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'entries/:entryId',
                element: (
                  <RequireAuth>
                    <EntryDetailPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'entrants',
                element: (
                  <RequireAuth>
                    <EntrantsPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'entrants/:exhibitorId',
                element: (
                  <RequireAuth>
                    <EntrantPage />
                  </RequireAuth>
                ),
              },
              { path: 'status', element: <StatusPage /> },
              {
                path: 'reports',
                element: (
                  <RequireAuth>
                    <ReportsPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'reports/club-participation',
                element: (
                  <RequireAuth>
                    <ClubParticipationPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'reports/club-participation/view',
                element: (
                  <RequireAuth>
                    <ClubParticipationPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'reports/awards/cards',
                element: (
                  <RequireAuth>
                    <MemberAwardCardsPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'reports/layout-demo',
                element: (
                  <RequireAuth>
                    <ReportLayoutDemoPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'judging',
                element: (
                  <RequireAuth>
                    <JudgingPage />
                  </RequireAuth>
                ),
              },
            ],
          },
        ],
      },
      {
        element: <LabelOnlyAllowed />,
        children: [{ path: 'run/board/present', element: <ShowDayBoardPage presentation /> }],
      },
    ],
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <LandingPage /> },
      { path: 'shows', element: <ShowsPage /> },
      {
        path: 'admin/show-import',
        element: (
          <RequireAuth adminOnly>
            <ShowImportPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/show-transfer',
        element: (
          <RequireAuth adminOnly>
            <ShowTransferPage />
          </RequireAuth>
        ),
      },
      { path: 'show-series', element: <ShowSeriesPage /> },
      { path: 'show-series/for-show/:showId', element: <ShowSeriesRedirect /> },
      // An index route adds ranking priority over /:showSlug/setup for the slug show-series.
      { path: 'show-series/:seriesId', element: <Navigate replace to="reports" /> },
      {
        path: 'show-series/:seriesId',
        element: <SeriesLayout />,
        children: [
          { path: 'reports', element: <SeriesTrendReportPage /> },
          { path: 'alignment', element: <SeriesAlignmentPage /> },
          { path: 'settings', element: <SeriesSettingsPage /> },
        ],
      },
      {
        path: 'shows/new',
        element: (
          <RequireAuth adminOnly>
            <NewShowWizard />
          </RequireAuth>
        ),
      },
      {
        path: 'organizations',
        element: (
          <RequireAuth adminOnly>
            <OrganizationsPage />
          </RequireAuth>
        ),
      },
      {
        path: 'people',
        element: (
          <RequireAuth adminOnly>
            <PeoplePage />
          </RequireAuth>
        ),
      },
      { path: 'clubs', element: <ClubsPage /> },
      { path: 'setup', element: <LegacyShowRedirect /> },
      { path: 'classes', element: <LegacyShowRedirect /> },
      { path: 'catalog/classes', element: <ModelClassesPage /> },
      { path: 'exhibitors', element: <LegacyShowRedirect /> },
      {
        path: 'entries',
        element: (
          <RequireAuth>
            <LegacyShowRedirect />
          </RequireAuth>
        ),
      },
      { path: 'entries/:entryId', element: <LegacyShowRedirect /> },
      { path: 'entrants', element: <LegacyShowRedirect /> },
      { path: 'entrants/:exhibitorId', element: <LegacyShowRedirect /> },
      { path: 'status', element: <LegacyShowRedirect /> },
      { path: 'check-in', element: <LegacyShowRedirect /> },
      {
        path: 'judging',
        element: (
          <RequireAuth>
            <LegacyShowRedirect />
          </RequireAuth>
        ),
      },
      {
        path: 'awards',
        element: (
          <RequireAuth>
            <LegacyShowRedirect />
          </RequireAuth>
        ),
      },
      {
        path: 'reports',
        element: (
          <RequireAuth>
            <LegacyShowRedirect />
          </RequireAuth>
        ),
      },
      {
        path: 'reports/club-participation',
        element: (
          <RequireAuth>
            <LegacyShowRedirect />
          </RequireAuth>
        ),
      },
      {
        path: 'reports/awards/cards',
        element: (
          <RequireAuth>
            <LegacyShowRedirect />
          </RequireAuth>
        ),
      },
      {
        path: 'reports/layout-demo',
        element: (
          <RequireAuth>
            <LegacyShowRedirect />
          </RequireAuth>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <RequireAuth adminOnly>
            <AdminUsersPage />
          </RequireAuth>
        ),
      },
      { path: 'account', element: <MyAccountPage /> },
      { path: 'account/change-password', element: <ChangePasswordPage /> },
      { path: 'run', element: <LegacyShowRedirect /> },
    ],
  },
  {
    path: '/print/reports/hero-award',
    element: (
      <RequireAuth>
        <HeroAwardReportPage />
      </RequireAuth>
    ),
  },
  {
    path: '/print/reports/judge-speaking',
    element: (
      <RequireAuth>
        <JudgeSpeakingReportPage />
      </RequireAuth>
    ),
  },
  {
    path: '/print/reports/display',
    element: (
      <RequireAuth>
        <DisplayResultsReportPage />
      </RequireAuth>
    ),
  },
  {
    path: '/print/reports/sharing',
    element: (
      <RequireAuth>
        <ShowResultsReportPage />
      </RequireAuth>
    ),
  },
  {
    path: '/print/reports/club-participation',
    element: (
      <RequireAuth>
        <ClubParticipationReportPage />
      </RequireAuth>
    ),
  },
  {
    path: '/print/reports/staff-roster',
    element: (
      <RequireAuth>
        <StaffRosterReportPage />
      </RequireAuth>
    ),
  },
  {
    path: '/print/reports/certificates/staff',
    element: (
      <RequireAuth>
        <StaffCertificatesReportPage />
      </RequireAuth>
    ),
  },
  { path: '*', element: <NotFoundPage /> },
]

export const router = createBrowserRouter(routes)
